import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { mumsApi } from "./mumsApi";
import { MumDrawer, PhotoField, FormSection } from "./MumDrawer";
import { Plus, Search, Edit3, Trash2, Loader2, Flower2 } from "lucide-react";
import { toast } from "sonner";

const BLOOM_STATUSES = ["Growing", "Budding", "Flowering", "Finished", "Lifted"];
const STATUS_TONES = {
  Growing: "bg-[var(--mum-sky)] text-[var(--mum-sapphire)]",
  Budding: "bg-[var(--mum-cornflower)]/30 text-[var(--mum-sapphire)]",
  Flowering: "bg-[var(--mum-cobalt)] text-[var(--mum-ivory)]",
  Finished: "bg-[var(--mum-silver)] text-[var(--mum-cobalt)]",
  Lifted: "bg-[var(--mum-sapphire)]/20 text-[var(--mum-sapphire)]",
};
const empty = {
  variety_name: "", photos: [], bloom_size: "", plant_height: "", garden_bed: "", row: "",
  date_planted: "", status: "Growing", stem_value: 0, notes: "", estimated_stems: 0,
};

export default function MumsBlooms() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [drawer, setDrawer] = useState(() =>
    new URLSearchParams(window.location.search).get("add") === "1" ? "new" : null
  );

  const load = async () => { setLoading(true); try { setItems(await mumsApi.listBlooms()); } finally { setLoading(false); } };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await mumsApi.listBlooms();
        if (!cancelled) setItems(r);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    if (params.get("add") === "1") {
      params.delete("add");
      setParams(params, { replace: true });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((i) => (filter ? i.status === filter : true))
      .filter((i) => (q ? [i.variety_name, i.garden_bed].some((s) => (s || "").toLowerCase().includes(q)) : true));
  }, [items, search, filter]);

  const onDelete = async (id) => {
    if (!window.confirm("Remove this bloom record?")) return;
    await mumsApi.deleteBloom(id);
    toast.success("Bloom removed");
    await load();
  };

  return (
    <div className="space-y-6" data-testid="mums-blooms-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="mum-chip"><Flower2 size={12} /> Tab 2</div>
          <h1 className="font-display text-4xl mt-2">My Dahlia Blooms</h1>
          <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">{filtered.length} record{filtered.length === 1 ? "" : "s"}</p>
        </div>
        <button className="mum-btn" onClick={() => setDrawer("new")} data-testid="mums-bloom-add"><Plus size={15} /> Add Bloom</button>
      </header>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mum-cobalt)]/60" />
          <input className="mum-field pl-9" placeholder="Search by variety or bed…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mums-bloom-search" />
        </div>
        <select className="mum-field sm:w-56" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All statuses</option>
          {BLOOM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="mums-card-quiet p-10 text-center" data-testid="mums-bloom-empty">
          <Flower2 size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
          <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">No blooms recorded yet.</p>
          <button className="mum-btn mt-5" onClick={() => setDrawer("new")} data-testid="mums-bloom-add-first"><Plus size={15} /> Add first bloom</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mums-bloom-grid">
          {filtered.map((b) => (
            <article key={b.id} className="mums-card overflow-hidden flex flex-col" data-testid={`bloom-${b.id}`}>
              <div className="aspect-[4/3] bg-[var(--mum-sky)]/50">
                {(b.photos || [])[0] ? (
                  <img src={b.photos[0]} alt={b.variety_name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Flower2 size={36} className="text-[var(--mum-cobalt)]/50" /></div>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-xl text-[var(--mum-sapphire)]">{b.variety_name}</h3>
                  <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TONES[b.status] || "bg-[var(--mum-sky)]"}`}>{b.status}</span>
                </div>
                <p className="text-xs text-[var(--mum-cobalt)]/75 mt-1">
                  {[b.bloom_size, b.plant_height && `${b.plant_height} tall`, b.garden_bed && `Bed ${b.garden_bed}`].filter(Boolean).join(" · ")}
                </p>
                {b.first_bloom_date && <p className="text-xs text-[var(--mum-cobalt)] mt-1">First bloom: {b.first_bloom_date}</p>}
                {b.notes && <p className="text-sm mt-2 line-clamp-3">{b.notes}</p>}
                <div className="flex gap-2 mt-auto pt-3">
                  <button className="mum-btn-ghost flex-1" onClick={() => setDrawer({ edit: b })} data-testid={`bloom-edit-${b.id}`}><Edit3 size={13} /> Edit</button>
                  <button className="mum-btn-ghost" onClick={() => onDelete(b.id)} data-testid={`bloom-delete-${b.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <MumDrawer open={drawer !== null} onClose={() => setDrawer(null)} title={drawer?.edit ? "Edit Bloom" : "Add Bloom"} testid="mums-bloom-drawer">
        <BloomForm initial={drawer?.edit || empty} onClose={() => setDrawer(null)} onSaved={async () => { setDrawer(null); await load(); }} />
      </MumDrawer>
    </div>
  );
}

function BloomForm({ initial, onClose, onSaved }) {
  const editing = !!initial.id;
  const [form, setForm] = useState({ ...empty, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.variety_name.trim()) { toast.error("Variety name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, stem_value: Number(form.stem_value) || 0, estimated_stems: Number(form.estimated_stems) || 0 };
      if (editing) await mumsApi.updateBloom(initial.id, payload);
      else await mumsApi.createBloom(payload);
      toast.success(editing ? "Bloom updated" : "Bloom added");
      onSaved();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't save the bloom");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="bloom-form">
      <FormSection title="The Variety">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Variety name *</span>
          <input className="mum-field mt-1.5" value={form.variety_name} onChange={(e) => set("variety_name", e.target.value)} required data-testid="bloom-variety-name" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Status</span>
            <select className="mum-field mt-1.5" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="bloom-status">
              {BLOOM_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Date planted</span>
            <input type="date" className="mum-field mt-1.5" value={form.date_planted || ""} onChange={(e) => set("date_planted", e.target.value)} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Bloom size</span>
            <input className="mum-field mt-1.5" placeholder="e.g. Dinnerplate, Ball, 15 cm" value={form.bloom_size || ""} onChange={(e) => set("bloom_size", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Plant height</span>
            <input className="mum-field mt-1.5" placeholder="e.g. 120 cm" value={form.plant_height || ""} onChange={(e) => set("plant_height", e.target.value)} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Bed</span>
            <input className="mum-field mt-1.5" value={form.garden_bed || ""} onChange={(e) => set("garden_bed", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Row</span>
            <input className="mum-field mt-1.5" value={form.row || ""} onChange={(e) => set("row", e.target.value)} />
          </label>
        </div>
      </FormSection>

      <FormSection title="Performance">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Estimated stems</span>
            <input type="number" min="0" className="mum-field mt-1.5" value={form.estimated_stems} onChange={(e) => set("estimated_stems", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Stem value ($)</span>
            <input type="number" min="0" step="0.5" className="mum-field mt-1.5" value={form.stem_value} onChange={(e) => set("stem_value", e.target.value)} />
          </label>
        </div>
      </FormSection>

      <FormSection title="Photos">
        <PhotoField label="Bloom photos" value={form.photos} onChange={(v) => set("photos", v)} multi testid="bloom-photos" />
      </FormSection>

      <FormSection title="Notes">
        <textarea className="mum-field" rows={4} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="How is she behaving?" />
      </FormSection>

      <div className="flex gap-2 sticky bottom-0 bg-[var(--mum-paper)] pt-3 -mx-5 px-5 pb-1 border-t border-[var(--mum-sky)]">
        <button type="button" className="mum-btn-ghost flex-1" onClick={onClose}>Cancel</button>
        <button type="submit" className="mum-btn flex-1" disabled={busy} data-testid="bloom-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {editing ? "Save changes" : "Add bloom"}
        </button>
      </div>
    </form>
  );
}
